import { useState, useEffect, useCallback } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { apiPost } from '../lib/api-mutations.js';
import { useToast } from '../hooks/useToast.js';
import type { PlaybookListItem, PlaybookListResponse, ImpactMeasurement, ImpactListResponse } from '../types/index.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { Select } from '../components/Select.js';
import { EmptyState } from '../components/EmptyState.js';
import { Region, RegionHead } from '../components/ui/Region.js';
import { Chip } from '../components/ui/Chip.js';
import { MeasureBar } from '../components/ui/MeasureBar.js';
import { StageLoop } from '../components/how-it-works/diagrams/LoopDiagram.js';

/* Badges on this page take the chip-tone mapping settled in spec 235's
   evidence.md rather than a freshly derived one — deciding it twice produces
   two answers. green/emerald → ok, amber/yellow/orange → warn, red/rose →
   fail, teal/blue/sky → info, and slate/gray is not a tone at all but a bare
   chip for a genuinely neutral state. Every chip keeps its word: the tone is
   reinforcement on top of a label that is already legible without colour. */

function SignificanceBadge({ value }: { value: boolean | null }) {
  if (value === null) return <Chip>Insufficient data</Chip>;
  if (value) return <Chip tone="ok">Significant</Chip>;
  return <Chip tone="warn">Not significant</Chip>;
}

function DeltaArrow({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return <Icon name="bx-minus" className="text-gray-300" />;
  if (value > 0) return <Icon name="bx-trending-up" className="text-green-600" />;
  if (value < 0) return <Icon name="bx-trending-down" className="text-red-600" />;
  return <Icon name="bx-minus" className="text-gray-400" />;
}

// `moderate` and `weak` both land on `warn` because yellow and orange share
// that bucket. Spec 235's rule for a collision like this: the label stays the
// state's own short name, never a generic placeholder — so the two remain
// distinguishable by their word even though they share a tone.
const ATTRIBUTION_TONES: Record<string, 'ok' | 'warn' | 'fail'> = {
  strong: 'ok',
  moderate: 'warn',
  weak: 'warn',
  confounded: 'fail',
};

function AttributionBadge({ strength }: { strength: string }) {
  return <Chip tone={ATTRIBUTION_TONES[strength]}>{strength}</Chip>;
}

// Spec 089d — Search Console corroboration badge.
// Renders teal "GSC corroborated" when both GEO Observatory and Google agree on direction,
// amber "GSC disagrees" when confounding_factors contains 'gsc_disagrees',
// or null when no corroboration data is available.
interface GscSurfaceDelta {
  impressions_delta: number;
  clicks_delta: number;
  position_delta: number;
}
interface GscCorroborationShape {
  ai_overview: GscSurfaceDelta | null;
  ai_mode: GscSurfaceDelta | null;
  window_days: number;
  samples_before: number;
  samples_after: number;
}

function GscCorroborationBadge({
  corroboration,
  confoundingFactors,
}: {
  corroboration: GscCorroborationShape | null | undefined;
  confoundingFactors: string[] | undefined;
}) {
  if (!corroboration) return null;
  const disagrees = (confoundingFactors ?? []).includes('gsc_disagrees');

  // Aggregate sign across surfaces (impressions + clicks).
  const aiOv = corroboration.ai_overview;
  const aiMo = corroboration.ai_mode;
  let total = 0;
  if (aiOv) total += aiOv.impressions_delta + aiOv.clicks_delta;
  if (aiMo) total += aiMo.impressions_delta + aiMo.clicks_delta;
  const hasPositive = total > 0;

  if (!disagrees && !hasPositive) return null;

  const fmtSurface = (s: GscSurfaceDelta | null): string =>
    s == null ? 'no data' :
    `imp ${s.impressions_delta >= 0 ? '+' : ''}${s.impressions_delta}, clicks ${s.clicks_delta >= 0 ? '+' : ''}${s.clicks_delta}`;

  const tooltip =
    `Search Console (${corroboration.window_days}d window, ${corroboration.samples_before}→${corroboration.samples_after} rows):\n` +
    `  ai_overview: ${fmtSurface(aiOv)}\n` +
    `  ai_mode: ${fmtSurface(aiMo)}`;

  // amber → warn, teal → info. The glyph stays in front of the word rather
  // than replacing it: the tone is reinforcement, never the only carrier.
  if (disagrees) {
    return (
      <Chip
        tone="warn"
        className="ml-2"
        data-testid="gsc-corroboration-badge"
        data-state="disagrees"
        title={tooltip}
      >
        ⚠ GSC disagrees
      </Chip>
    );
  }
  return (
    <Chip
      tone="info"
      className="ml-2"
      data-testid="gsc-corroboration-badge"
      data-state="corroborated"
      title={tooltip}
    >
      ✓ GSC corroborated
    </Chip>
  );
}

function formatMetricValue(value: unknown): string {
  if (value === null || value === undefined) return '--';
  if (typeof value === 'number') return value.toFixed(2);
  return String(value);
}

function BeforeAfterTable({ measurement }: { measurement: ImpactMeasurement }) {
  const baseline = measurement.baseline_scores as Record<string, unknown> | null;
  const current = measurement.current_scores as Record<string, unknown> | null;
  const delta = measurement.delta as Record<string, unknown> | null;

  if (!baseline && !current) {
    return <p className="text-sm text-gray-400">No score data available.</p>;
  }

  // Extract visibility_scores from the snapshots if nested
  const baseScores = (baseline as Record<string, unknown>)?.visibility_scores as Record<string, unknown>[] | undefined;
  const currScores = (current as Record<string, unknown>)?.visibility_scores as Record<string, unknown>[] | undefined;

  // Simple key-value comparison from delta
  if (delta && typeof delta === 'object') {
    const entries = Object.entries(delta);
    if (entries.length > 0) {
      return (
        <div className="overflow-x-auto">
          <table className="hub-table" data-testid="before-after-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th className="is-num">Baseline</th>
                <th className="is-num">Current</th>
                <th className="is-num">Delta</th>
                <th className="text-center">Direction</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([key, val]) => {
                const deltaVal = typeof val === 'number' ? val : null;
                const baseVal = baseline ? (baseline as Record<string, unknown>)[key] : null;
                const currVal = current ? (current as Record<string, unknown>)[key] : null;
                return (
                  <tr key={key}>
                    <td className="font-medium">{key.replace(/_/g, ' ')}</td>
                    <td className="is-num">{formatMetricValue(baseVal)}</td>
                    <td className="is-num">{formatMetricValue(currVal)}</td>
                    <td className="is-num">
                      {deltaVal !== null ? (deltaVal > 0 ? '+' : '') + deltaVal.toFixed(2) : '--'}
                    </td>
                    <td className="text-center"><DeltaArrow value={deltaVal} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  }

  return <p className="text-sm text-gray-400">Delta data not available.</p>;
}

export function ImpactPage() {
  const { selectedBrand, user } = useBrand();
  const { addToast } = useToast();
  const [playbooks, setPlaybooks] = useState<PlaybookListItem[]>([]);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<ImpactMeasurement[]>([]);
  const [loading, setLoading] = useState(false);
  const [measLoading, setMeasLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rebaselining, setRebaselining] = useState(false);
  const [bursting, setBursting] = useState(false);

  // Fetch playbooks
  useEffect(() => {
    if (!selectedBrand || !user) return;
    let cancelled = false;
    setLoading(true);
    async function load() {
      try {
        const url = `/api/v1/tenants/${selectedBrand!.tenant_id}/brands/${selectedBrand!.id}/playbooks?limit=100`;
        const result = await apiGet<PlaybookListResponse>(url);
        if (!cancelled) {
          setPlaybooks(result.data);
          // Auto-select first active/completed playbook
          const preferred = result.data.find((p) => p.status === 'active' || p.status === 'completed') ?? result.data[0];
          if (preferred && !selectedPlaybookId) setSelectedPlaybookId(preferred.id);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load playbooks');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedBrand, user]);

  // Fetch measurements when playbook selected
  const fetchMeasurements = useCallback(async () => {
    if (!selectedPlaybookId) return;
    setMeasLoading(true);
    try {
      const url = `/api/v1/playbooks/${selectedPlaybookId}/impact`;
      const result = await apiGet<ImpactListResponse>(url);
      setMeasurements(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load measurements');
    } finally {
      setMeasLoading(false);
    }
  }, [selectedPlaybookId]);

  useEffect(() => { fetchMeasurements(); }, [fetchMeasurements]);

  // Re-baseline handler (074)
  const handleRebaseline = async () => {
    if (!selectedPlaybookId) return;
    setRebaselining(true);
    try {
      await apiPost(`/api/v1/playbooks/${selectedPlaybookId}/rebaseline`, {});
      addToast('Baseline updated to current scores', 'success');
      fetchMeasurements();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Re-baseline failed', 'error');
    } finally {
      setRebaselining(false);
    }
  };

  // Impact burst handler (073)
  const handleBurst = async () => {
    if (!selectedPlaybookId) return;
    setBursting(true);
    try {
      const result = await apiPost<{ data: { burst_jobs_created: number; measurement_id: string } }>(
        `/api/v1/playbooks/${selectedPlaybookId}/impact/burst`,
        { samples_per_prompt: 50 },
      );
      addToast(`Burst scheduled: ${result.data.burst_jobs_created} jobs created`, 'success');
      fetchMeasurements();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Burst scheduling failed', 'error');
    } finally {
      setBursting(false);
    }
  };

  if (!selectedBrand) {
    return (
      <div className="hub-page--dashboard" data-density="compact">
        <Region>
          <h1 className="hub-page__title">Impact</h1>
        </Region>
        <Region>
          <EmptyState
            icon="bx-bar-chart-alt-2"
            title="Select a brand"
            description="Choose a brand to see the before/after measurement of its playbooks."
          />
        </Region>
      </div>
    );
  }

  return (
    /* Spec 236 — the DASHBOARD archetype. The page is a measurement header, an
       actions-progress figure, a five-column before/after table and an
       attribution list: summary first, then detail, regions that touch with a
       hairline between them (`.hub-page--dashboard > * + *` in layout.css).
       No page-root gap class, because on this archetype the hairline IS the
       separation — a gap as well would make the regions cards again. */
    <div data-testid="impact-page" className="hub-page--dashboard" data-density="compact">
      <Region>
        <div className="hub-page__head">
          <div>
            <h1 className="hub-page__title">Impact</h1>
            <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
              Before/after comparison for {selectedBrand.name}
            </p>
            {/* Spec 256: the loop as navigation — this page's stage inverted, the next one click away. */}
            <div className="mt-3">
              <StageLoop current="track" />
            </div>
          </div>
        </div>
      </Region>

      {error && (
        <Region>
          <div className="flex flex-wrap items-center gap-2 text-sm text-red-700">
            <Chip tone="fail">Error</Chip>
            {error}
          </div>
        </Region>
      )}

      {loading && (
        <Region>
          <div className="flex justify-center"><Spinner className="text-violet-600" /></div>
        </Region>
      )}

      {/* Gate 120.A.3 counts `EmptyState` in this file and wants at least two,
          and before this spec it sat at exactly two — the import plus one
          call. The obvious restyle is to route both empty states through one
          shared call; that is precisely what breaks it. So the no-playbooks
          state, which was a hand-rolled blue box, becomes a SECOND EmptyState
          call rather than being folded into the first. */}
      {!loading && playbooks.length === 0 && (
        <Region data-testid="no-playbooks">
          <EmptyState
            icon="bx-bar-chart-alt-2"
            title="Complete playbook actions to measure impact"
            description="Generate and complete a playbook first, then track the before/after results here."
          />
          <div className="mt-4 flex justify-center">
            <a className="hub-btn hub-btn--primary" href="/playbooks">
              Go to playbooks
            </a>
          </div>
        </Region>
      )}

      {!loading && playbooks.length > 0 && (
        <>
          {/* Playbook selector. A non-native Select — gate 232.A.5 sums the
              native element across five files and requires zero, three of them
              shipped by spec 232, so a slip here fails a gate naming files
              this spec never touched. */}
          <Region>
            <RegionHead title="Playbook" caption="Impact is measured per playbook" />
            <div className="hub-toolbar">
              <Select
                options={playbooks.map((pb) => ({ value: pb.id, label: `${pb.title} (${pb.status})` }))}
                value={selectedPlaybookId ?? ''}
                onChange={(v) => setSelectedPlaybookId(v || null)}
                placeholder="Select a playbook"
                className="w-80"
                data-testid="playbook-selector"
              />
              <Button
                variant="primary"
                onClick={handleBurst}
                disabled={bursting || !selectedPlaybookId}
                data-testid="burst-button"
              >
                {bursting ? <Spinner /> : <Icon name="bx-bolt-circle" />}
                Measure impact (burst)
              </Button>
              <Button
                variant="ghost"
                onClick={handleRebaseline}
                disabled={rebaselining || !selectedPlaybookId}
                data-testid="rebaseline-button"
              >
                {rebaselining ? <Spinner /> : <Icon name="bx-refresh" />}
                Re-baseline
              </Button>
            </div>
          </Region>

          {/* Measurements */}
          {measLoading && (
            <Region>
              <div className="flex justify-center"><Spinner className="text-violet-600" /></div>
            </Region>
          )}

          {!measLoading && measurements.length === 0 && (
            <Region data-testid="no-measurements">
              <EmptyState
                icon="bx-bar-chart-alt-2"
                variant="no-results"
                title="No measurements yet"
                description="Trigger an impact measurement to see before/after data."
              />
            </Region>
          )}

          {!measLoading && measurements.map((m) => (
            <Region key={m.id} data-testid={`measurement-${m.id}`}>
              {/* Model Update Warning (SR-4) */}
              {m.model_update_detected && (
                <div className="mb-4 flex items-start gap-3 border border-amber-200 bg-amber-50 p-4" data-testid="model-update-warning">
                  <Icon name="bx-error" className="mt-0.5 flex-shrink-0 text-amber-600" />
                  <div>
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-amber-800">
                      <Chip tone="warn">Confounded</Chip>
                      Model update detected
                    </p>
                    <p className="text-sm text-amber-700">Results may be confounded by engine model version changes during the measurement period.</p>
                    {m.model_update_warnings && m.model_update_warnings.length > 0 && (
                      <ul className="mt-1 list-disc pl-5 text-sm text-amber-700">
                        {m.model_update_warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {/* Measurement Header */}
              <RegionHead
                title={`Measured: ${m.measured_at ? new Date(m.measured_at).toLocaleString() : 'Pending'}`}
                caption={`Status: ${m.status}`}
                action={<SignificanceBadge value={m.statistical_significance} />}
              />

              {/* Actions Progress (FR-7) — the house measure bar: a light
                  column for the range and a dash at the value, rather than a
                  filled pill. */}
              {m.actions_total !== null && m.actions_total > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-[var(--hub-color-ink-muted)]">
                    <span>Actions progress</span>
                    <span>{m.actions_completed ?? 0} / {m.actions_total}</span>
                  </div>
                  <MeasureBar
                    className="mt-2"
                    emphasize
                    fraction={(m.actions_completed ?? 0) / m.actions_total}
                  />
                </div>
              )}

              {/* Before/After Table (FR-3) */}
              {m.status === 'completed' && <BeforeAfterTable measurement={m} />}

              {/* Attribution Section (FR-6) */}
              {m.attribution && Object.keys(m.attribution).length > 0 && (
                <div className="mt-6" data-testid="attribution-section">
                  <h3 className="mb-3 text-sm font-semibold text-[var(--hub-color-ink)]">Attribution</h3>
                  <div className="divide-y divide-[var(--hub-color-line)] border-y border-[var(--hub-color-line)]">
                    {(() => {
                      const attr = m.attribution as Record<string, unknown>;
                      // Spec 031 + 089d shape: { overall, details: [...] }
                      const details = Array.isArray(attr?.details)
                        ? (attr.details as Array<Record<string, unknown>>)
                        : null;
                      if (details && details.length > 0) {
                        return details.map((d, i) => {
                          const level = String(d.level ?? 'unknown');
                          const action_type = String(d.action_type ?? '');
                          const engine = d.engine != null ? String(d.engine) : null;
                          const corroboration = (d.gsc_corroboration ?? null) as GscCorroborationShape | null;
                          const factors = Array.isArray(d.confounding_factors)
                            ? (d.confounding_factors as string[])
                            : undefined;
                          const label = engine
                            ? `${action_type} → ${engine}`
                            : action_type || `action ${i + 1}`;
                          return (
                            <div
                              key={String(d.action_id ?? i)}
                              className="flex items-center justify-between gap-3 px-3 py-2"
                              data-testid="attribution-detail-row"
                            >
                              <span className="text-sm text-[var(--hub-color-ink)]">{label}</span>
                              <span className="flex items-center">
                                <AttributionBadge strength={level} />
                                <GscCorroborationBadge
                                  corroboration={corroboration}
                                  confoundingFactors={factors}
                                />
                              </span>
                            </div>
                          );
                        });
                      }
                      // Legacy shape fallback.
                      return Object.entries(m.attribution).map(([key, val]) => {
                        const attrEntry = val as Record<string, unknown>;
                        return (
                          <div key={key} className="flex items-center justify-between gap-3 px-3 py-2">
                            <span className="text-sm text-[var(--hub-color-ink)]">{key.replace(/_/g, ' ')}</span>
                            <AttributionBadge strength={String(attrEntry?.strength ?? attrEntry ?? 'unknown')} />
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </Region>
          ))}
        </>
      )}
    </div>
  );
}
